/**
 * Pure rules for the breakfast event form. No React and no network calls.
 *
 * `audiencia` is one stored value, not two flags:
 * 0 members (socios), 1 non-members, 2 both.
 * The API may send those as strings, so every check accepts either form.
 * Clearing both checkboxes stores null.
 *
 * Duration text is `H:MM` or `HH:MM`, from 0:00 through 24:59.
 * 0:00 is parsed but rejected: the event has to last at least one minute.
 *
 * Maximum capacity may be 0. An empty string or a non-numeric value does not count as filled in.
 *
 * The banner limit is 5 MiB and the types are jpeg, jpg, and png, matching a buyer-company logo.
 * Host users are loaded with role id "13".
 *
 * Save is disabled while a request is in flight, while the form is still loading, or while the name
 * duplicates another breakfast event. A new event can be saved as soon as the form is complete.
 * An existing event also needs an unsaved change, so opening an event and saving immediately does nothing.
 *
 * The submit payload formats the event date and the three registration dates as YYYY-MM-DD.
 * A registration date that is still empty is sent as null. The image name and MIME type are sent
 * only when the user picked a new banner in this session.
 */
export const DATE_FORMAT = "YYYY-MM-DD";
export const MINUTES_PER_HOUR = 60;
export const AUDIENCE_MEMBERS = 0;
export const AUDIENCE_NON_MEMBERS = 1;
export const AUDIENCE_BOTH = 2;
export const AUDIENCE_MEMBERS_KEY = "socios";
export const AUDIENCE_NON_MEMBERS_KEY = "noSocios";
const BYTES_PER_KILOBYTE = 1024;
const KILOBYTES_PER_MEGABYTE = 1024;
const MAX_BANNER_MEGABYTES = 5;
export const MAX_BANNER_BYTES =
  MAX_BANNER_MEGABYTES * KILOBYTES_PER_MEGABYTE * BYTES_PER_KILOBYTE;
export const BANNER_MIME_TYPES = ["image/jpeg", "image/jpg", "image/png"];
export const BREAKFAST_EVENTS_PATH = "/breakfastEvents";
export const UNEXPECTED_SUBMIT_RESULT = "Unexpected breakfast submit result";
export const HOST_ROLE_ID = "13";

export const DURATION_INPUT_PATTERN = /^([0-9]|1[0-9]|2[0-4]):[0-5][0-9]$/;

export const INITIAL_BREAKFAST_FORM = {
  idUsuarioAnfitrion: null,
  fechaLimiteAccesoAnfitrion: null,
  imagenBanner: null,
  nombre: "",
  fechaEvento: null,
  descripcion: "",
  horaInicio: null,
  duracion: null,
  audiencia: null,
  fechaDisponibleSocios: null,
  fechaDisponibleNoSocios: null,
  fechaLimiteRegistro: null,
  ubicacion: "",
  cupoMaximo: null,
  activo: true,
  nombreConferencista: "",
  puestoConferencista: "",
  nombreEmpresaConferencista: "",
  tituloConferencia: "",
  tamanosDeLaEmpresa: null,
  sectores: null,
  subSectores: null,
  ramas: null,
};

/**
 * Trims a duration field for validation.
 * @param {string|null|undefined} value - Raw duration from the form or the API.
 * @returns {string|null} Trimmed text, or null when the field is empty.
 */
const trimDuration = (value) => {
  if (value == null) {
    return null;
  }
  const s = String(value).trim();
  return s === "" ? null : s;
};

/**
 * Converts a duration string into total minutes.
 * @param {string} s - Duration already checked against `DURATION_INPUT_PATTERN`.
 * @returns {number|null} Minutes, or null when the text is not `H:MM`.
 */
const parseDurationMinutes = (s) => {
  if (!DURATION_INPUT_PATTERN.test(s)) {
    return null;
  }
  const [h, m] = s.split(":").map(Number);
  return h * MINUTES_PER_HOUR + m;
};

/**
 * Accepts the numeric audience code or the same code as a string from the API.
 * @param {number|string|null|undefined} audiencia - Current audience value.
 * @param {number} numericValue - One of the audience constants.
 * @returns {boolean} True when both forms of the code match.
 */
const audienceMatches = (audiencia, numericValue) =>
  audiencia === numericValue || audiencia === String(numericValue);

/**
 * Whether at least one audience side is selected.
 * @param {number|string|null|undefined} audiencia - Current audience value.
 * @returns {boolean} True for members, non-members, or both.
 */
export const isAudienciaChosen = (audiencia) =>
  audienceMatches(audiencia, AUDIENCE_MEMBERS) ||
  audienceMatches(audiencia, AUDIENCE_NON_MEMBERS) ||
  audienceMatches(audiencia, AUDIENCE_BOTH);

/**
 * Whether the members checkbox should be checked.
 * @param {number|string|null|undefined} audiencia - Current audience value.
 * @returns {boolean} True for members-only and for both.
 */
export const includesMembers = (audiencia) =>
  audienceMatches(audiencia, AUDIENCE_MEMBERS) ||
  audienceMatches(audiencia, AUDIENCE_BOTH);

/**
 * Whether the non-members checkbox should be checked.
 * @param {number|string|null|undefined} audiencia - Current audience value.
 * @returns {boolean} True for non-members-only and for both.
 */
export const includesNonMembers = (audiencia) =>
  audienceMatches(audiencia, AUDIENCE_NON_MEMBERS) ||
  audienceMatches(audiencia, AUDIENCE_BOTH);

/**
 * Toggles one audience checkbox and returns the single stored code.
 * @param {number|string|null|undefined} audiencia - Audience before the click.
 * @param {string} which - `socios` or `noSocios`.
 * @returns {number|null} 0, 1, 2, or null when both boxes end unchecked.
 */
export const nextBreakfastAudience = (audiencia, which) => {
  const sociosWas = includesMembers(audiencia);
  const noSociosWas = includesNonMembers(audiencia);
  const socios = which === AUDIENCE_MEMBERS_KEY ? !sociosWas : sociosWas;
  const noSocios =
    which === AUDIENCE_NON_MEMBERS_KEY ? !noSociosWas : noSociosWas;
  if (socios && noSocios) {
    return AUDIENCE_BOTH;
  }
  if (socios) {
    return AUDIENCE_MEMBERS;
  }
  if (noSocios) {
    return AUDIENCE_NON_MEMBERS;
  }
  return null;
};

/**
 * Whether a required text field has something other than whitespace.
 * @param {unknown} value - Field value.
 * @returns {boolean} True when the trimmed text is not empty.
 */
const hasText = (value) => String(value ?? "").trim() !== "";

/**
 * Whether the duration is a real event length.
 * Empty text and values outside `H:MM` fail. `0:00` matches the pattern but is rejected
 * because the event has to last at least one minute.
 * @param {string|null|undefined} duracion - Duration field.
 * @returns {boolean} True when the parsed length is greater than zero.
 */
const hasValidBreakfastDuration = (duracion) => {
  const durStr = trimDuration(duracion);
  if (durStr == null || !DURATION_INPUT_PATTERN.test(durStr)) {
    return false;
  }
  const durMinutes = parseDurationMinutes(durStr);
  return durMinutes != null && durMinutes > 0;
};

/**
 * Whether a host and the host access deadline are set.
 * @param {Object} formData - Breakfast form state.
 * @returns {boolean} True when both host fields are present.
 */
const hasBreakfastHostFields = (formData) =>
  Boolean(formData.idUsuarioAnfitrion) &&
  Boolean(formData.fechaLimiteAccesoAnfitrion);

/**
 * Whether maximum capacity has been entered.
 * Zero is allowed. Null, undefined, blank text, and non-numeric text are not.
 * @param {unknown} cupoMaximo - Capacity field.
 * @returns {boolean} True when the value is a finite number greater than or equal to zero.
 */
const isCupoMaximoFilled = (cupoMaximo) => {
  if (cupoMaximo === null || cupoMaximo === undefined) {
    return false;
  }
  const s = String(cupoMaximo).trim();
  if (s === "") {
    return false;
  }
  const n = Number(s);
  return Number.isFinite(n) && n >= 0;
};

/**
 * Whether the banner, name, date, time, description, location, and capacity are filled.
 * @param {Object} formData - Breakfast form state.
 * @returns {boolean} True when every general-information field needed to publish is present.
 */
const hasBreakfastEventFields = (formData) =>
  Boolean(formData.imagenBanner) &&
  hasText(formData.nombre) &&
  Boolean(formData.fechaEvento) &&
  Boolean(formData.horaInicio) &&
  hasText(formData.descripcion) &&
  hasText(formData.ubicacion) &&
  isCupoMaximoFilled(formData.cupoMaximo);

/**
 * Whether the four speaker fields are filled.
 * @param {Object} formData - Breakfast form state.
 * @returns {boolean} True when name, position, company, and conference title all have text.
 */
const hasBreakfastSpeakerFields = (formData) =>
  hasText(formData.nombreConferencista) &&
  hasText(formData.puestoConferencista) &&
  hasText(formData.nombreEmpresaConferencista) &&
  hasText(formData.tituloConferencia);

/**
 * Whether the three registration dates are set.
 * @param {Object} formData - Breakfast form state.
 * @returns {boolean} True when members, non-members, and the registration deadline all have a date.
 */
const hasBreakfastAvailabilityFields = (formData) =>
  Boolean(formData.fechaDisponibleSocios) &&
  Boolean(formData.fechaDisponibleNoSocios) &&
  Boolean(formData.fechaLimiteRegistro);

/**
 * Whether the form has every field required to publish.
 * @param {Object} formData - Breakfast form state.
 * @returns {boolean} True when host, event, audience, speaker, dates, and duration all pass.
 */
export const isBreakfastFormComplete = (formData) =>
  hasBreakfastHostFields(formData) &&
  hasBreakfastEventFields(formData) &&
  isAudienciaChosen(formData.audiencia) &&
  hasBreakfastSpeakerFields(formData) &&
  hasBreakfastAvailabilityFields(formData) &&
  hasValidBreakfastDuration(formData.duracion);

/**
 * Whether the publish button must stay disabled.
 * Saving, loading, or a duplicate name always disables it.
 * Edit mode also requires an unsaved change. A new event only requires a complete form.
 * @param {Object} params
 * @param {boolean} params.saving - A save request is in flight.
 * @param {boolean} params.loading - The form is still loading.
 * @param {boolean} params.nameIsValid - False when the name duplicates another breakfast event.
 * @param {boolean} params.isEditMode - True when an existing event is open.
 * @param {boolean} params.hasChanges - True after the user edits the loaded event.
 * @param {Object} params.formData - Breakfast form state.
 * @returns {boolean} True when publish should be disabled.
 */
export const isSaveDisabled = ({
  saving,
  loading,
  nameIsValid,
  isEditMode,
  hasChanges,
  formData,
}) => {
  if (saving || loading || !nameIsValid) {
    return true;
  }
  if (isEditMode) {
    return !hasChanges || !isBreakfastFormComplete(formData);
  }
  return !isBreakfastFormComplete(formData);
};

/**
 * Builds the body sent to the breakfast event API.
 * Registration dates that are still empty stay null. Image name and MIME type are included
 * only when the user picked a new banner in this session.
 * @param {Object} params
 * @param {Object} params.formData - Breakfast form state.
 * @param {boolean} params.imageChanged - True when a new banner was read in this session.
 * @param {string|null} params.imageName - File name of that banner.
 * @param {string|null} params.imageFormat - MIME type of that banner.
 * @param {string|null} params.userId - Signed-in user id.
 * @param {function(string): string} params.formatDate - Formats a date as `YYYY-MM-DD`.
 * @returns {Object} Payload for create or update.
 */
export const formatBreakfastSubmitPayload = ({
  formData,
  imageChanged,
  imageName,
  imageFormat,
  userId,
  formatDate,
}) => ({
  ...formData,
  nombreImagen: imageChanged ? imageName : null,
  formatoImagen: imageChanged ? imageFormat : null,
  userId,
  fechaEvento: formatDate(formData.fechaEvento),
  fechaDisponibleSocios: formData.fechaDisponibleSocios
    ? formatDate(formData.fechaDisponibleSocios)
    : null,
  fechaDisponibleNoSocios: formData.fechaDisponibleNoSocios
    ? formatDate(formData.fechaDisponibleNoSocios)
    : null,
  fechaLimiteRegistro: formData.fechaLimiteRegistro
    ? formatDate(formData.fechaLimiteRegistro)
    : null,
});
